import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import {
  ButtonType,
  IconPaths,
  KTSVG,
  SituacaoDiagnosticoType,
  showError,
} from '../../../../../../_metronic/helpers'
import { Button, LoadingIndicator, useModal, useToast } from '../../../../../components'
import { InfoAlert } from '../../../../../components/Info/InfoAlert'
import { useT } from '_metronic/i18n/Metronici18n'
import { AtendimentoDtoRespostaZerada, AtendimentoView } from '../../../atendimento/core/_models'
import {
  enviarFormulario,
  getFormularioAtendimento,
  getFormularioResolvido,
} from '../../../avaliacoes/avaliacoes-list/core/_requests'
import { FormularioAtendimentoList } from '../../../avaliacoes/avaliacoes-pendentes-list/core/_models'
import { RespostaEnvio } from '../../../formularios/formularios-create/core/_models'
import {
  FormularioPreenchimento,
  ValoresPreenchimento,
} from '../../../formularios/preenchimento/FormularioPreenchimento'
import { ValorCampo } from '../../../formularios/preenchimento/CampoPreenchimento'
import {
  CODIGO_RESPOSTAS_INVALIDAS,
  extrairErrosFormulario,
} from '../../../formularios/core/errosFormulario'
import {
  montarValoresIniciais,
  respostasZeradasDoEnvio,
} from '../../atendimento-video/modal/formulario-answer/FormularioModal'
import { useFinalizarParecer } from '../core/useFinalizarParecer'
import { pendenciaDiagnostico } from '../core/pendenciaDiagnostico'
import { LISTA_ATENDIMENTOS } from '../core/rotas'
import { DiagnosticoParecer } from './DiagnosticoParecer'
import styles from '../ParecerInterconsulta.module.css'

const FORM_ID = 'parecer-interconsulta-form'

type Props = {
  atendimento: AtendimentoView
  /** Instância do formulário em aberto (a da réplica, quando há réplica). */
  formulario: FormularioAtendimentoList
  /** Recarrega o atendimento (diagnóstico, estado e formulários). */
  onAtualizar: () => Promise<unknown>
}

const comoLista = (valor: ValorCampo | undefined): string[] =>
  Array.isArray(valor) ? [...valor].sort() : valor ? [valor] : []

/** Algum campo difere do gravado. Só para o aviso do rodapé: o envio refaz a conta por campo visível. */
const difere = (atuais: ValoresPreenchimento, gravados: ValoresPreenchimento) =>
  Object.keys(atuais).some(
    (codigo) =>
      comoLista(atuais[codigo]).join('\u0000') !== comoLista(gravados[codigo]).join('\u0000')
  )

/**
 * Parecer do teleconsultor numa tela só: as perguntas do formulário, o diagnóstico como última
 * seção e um rodapé fixo com "Finalizar e assinar", que grava as respostas, finaliza e assina e
 * volta para a lista. Certificado e e-SUS seguem como modais (vêm do `useFinalizarParecer`).
 *
 * As respostas só vão ao backend nesse envio (ele marca a instância como respondida). Se o
 * teleconsultor sair antes, o que foi digitado fica guardado na sessão (mesma chave do
 * `FormularioModal`) e volta na próxima abertura.
 */
export const ParecerEditor: FC<Props> = ({ atendimento, formulario, onAtualizar }) => {
  const { t } = useT()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { getPersistedData, persistData } = useModal()
  const finalizarParecer = useFinalizarParecer()

  const atendimentoId = Number(atendimento.id)
  const idFormularioAtendimento = Number(formulario.id)
  const chaveFormulario = ['parecer-formulario', atendimentoId, idFormularioAtendimento]
  const chavePersistencia = `formulario-${atendimentoId}-${idFormularioAtendimento}`

  const { data: formularioAtendimento, isLoading: carregandoRespostas } = useQuery(
    chaveFormulario,
    () => getFormularioAtendimento(atendimentoId, idFormularioAtendimento),
    { onError: showError, refetchOnWindowFocus: false }
  )
  const { data: formularioResolvido, isLoading: carregandoCampos } = useQuery(
    ['parecer-formulario-resolvido', atendimentoId, idFormularioAtendimento],
    () => getFormularioResolvido(atendimentoId, idFormularioAtendimento),
    { onError: showError, refetchOnWindowFocus: false }
  )

  const campos = useMemo(() => formularioResolvido?.campos ?? [], [formularioResolvido])
  const { valores, gravados, ajudas } = useMemo(
    () => montarValoresIniciais(campos, formularioAtendimento?.respostas ?? [], t),
    [campos, formularioAtendimento, t]
  )
  // Lido uma vez: o rascunho só inicializa o formulário, não acompanha a digitação.
  const [rascunho] = useState(
    () => getPersistedData(chavePersistencia) as ValoresPreenchimento | null
  )

  const [enviando, setEnviando] = useState(false)
  const [errosExternos, setErrosExternos] = useState<Record<string, string>>({})
  const [respostasZeradas, setRespostasZeradas] = useState<AtendimentoDtoRespostaZerada[]>([])
  const [aviso, setAviso] = useState<string | null>(null)
  const [faltaDiagnostico, setFaltaDiagnostico] = useState(false)
  const [situacaoNaTela, setSituacaoNaTela] = useState<SituacaoDiagnosticoType>()

  const valoresAtuais = useRef<ValoresPreenchimento | null>(null)
  const pendenteDeSalvar = useRef(false)
  const tituloDiagnostico = useRef<HTMLHeadingElement>(null)

  // Saiu da tela sem salvar: guarda o que foi digitado para a próxima abertura.
  useEffect(
    () => () => {
      if (pendenteDeSalvar.current && valoresAtuais.current) {
        persistData(chavePersistencia, valoresAtuais.current)
      }
    },
    [chavePersistencia, persistData]
  )

  const pendencia = pendenciaDiagnostico(atendimento, situacaoNaTela)

  const irParaDiagnostico = () => {
    tituloDiagnostico.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    tituloDiagnostico.current?.focus({ preventScroll: true })
  }

  /** Grava as respostas alteradas. `false` quando não dá para seguir (erro ou respostas zeradas). */
  const gravar = async (respostas: RespostaEnvio[]) => {
    // Nada mudou e já está respondido: o gravado vale, não há o que enviar.
    if (respostas.length === 0 && formularioAtendimento?.respondido) return true
    try {
      const resposta = await enviarFormulario(atendimentoId, idFormularioAtendimento, {
        respostas,
      })
      pendenteDeSalvar.current = false
      persistData(chavePersistencia, null)
      queryClient.invalidateQueries(chaveFormulario)

      const zeradas = respostasZeradasDoEnvio(resposta, idFormularioAtendimento)
      if (zeradas.length > 0) {
        // Salvou, mas o backend apagou respostas: o teleconsultor reconfere antes de seguir.
        setRespostasZeradas(zeradas)
        return false
      }
      return true
    } catch (error) {
      const estruturado = extrairErrosFormulario(error)
      if (estruturado?.code === CODIGO_RESPOSTAS_INVALIDAS) {
        const mapa: Record<string, string> = {}
        estruturado.codigosFaltantes.forEach((codigo) => {
          mapa[codigo] = t('formularios.preenchimento.obrigatorio')
        })
        estruturado.codigosInvalidos.forEach((codigo) => {
          mapa[codigo] = estruturado.message
        })
        setErrosExternos(mapa)
        return false
      }
      showError(error)
      return false
    }
  }

  const handleSubmit = async (respostas: RespostaEnvio[]) => {
    setAviso(null)
    setErrosExternos({})
    setRespostasZeradas([])
    setEnviando(true)
    try {
      if (!(await gravar(respostas))) return

      if (pendencia) {
        setFaltaDiagnostico(true)
        irParaDiagnostico()
        return
      }

      const resultado = await finalizarParecer(atendimentoId)
      if (resultado === 'semDiagnostico') {
        setFaltaDiagnostico(true)
        irParaDiagnostico()
        return
      }
      if (resultado === 'interrompido') {
        setAviso('Parecer salvo. A interconsulta não foi finalizada nem assinada.')
        return
      }
      if (resultado === 'naoAssinado') {
        // Fica na tela: recarregada, ela vira a etapa `aguardandoAssinatura`, com o botão de assinar.
        showToast({
          variant: 'warning',
          title: `Interconsulta finalizada, mas o parecer não foi assinado. Assine pelo botão "Assinar parecer" ou em ${t(
            'menu.avaliacoes'
          )} › ${t('menu.nav.avaliacoes_pendentes')}.`,
        })
        await onAtualizar()
        return
      }
      showToast({ variant: 'success', title: 'Parecer assinado. O solicitante foi avisado.' })
      navigate(LISTA_ATENDIMENTOS)
    } finally {
      setEnviando(false)
    }
  }

  if (carregandoRespostas || carregandoCampos) {
    return (
      <div className='d-flex justify-content-center py-10' role='status'>
        <LoadingIndicator />
        <span className='visually-hidden'>Carregando o formulário do parecer</span>
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-5'>
      <section aria-labelledby='parecer-formulario-titulo' className='card card-bordered'>
        <div className='card-body p-6'>
          <h2 id='parecer-formulario-titulo' className='fs-5 fw-bold mb-1'>
            {formularioResolvido?.nome ?? formulario.formulario?.nome ?? 'Parecer'}
          </h2>
          <p className='text-muted fs-7 mb-5'>
            Campos marcados com * são obrigatórios para finalizar.
          </p>
          <FormularioPreenchimento
            key={campos.length}
            id={FORM_ID}
            campos={campos}
            variaveis={formularioResolvido?.variaveis}
            valoresIniciais={rascunho ?? valores}
            valoresGravados={gravados}
            ajudas={ajudas}
            errosExternos={errosExternos}
            enviando={enviando}
            aviso={
              respostasZeradas.length > 0 ? (
                <div role='status' aria-live='polite'>
                  <InfoAlert
                    variant='warning'
                    title={t('formularios.preenchimento.zeradas.titulo')}
                  >
                    {t('formularios.preenchimento.zeradas.corpo', {
                      rotulos: respostasZeradas.map((zerada) => zerada.rotulo).join(', '),
                    })}
                  </InfoAlert>
                </div>
              ) : undefined
            }
            onValoresChange={(atuais) => {
              valoresAtuais.current = atuais
              const mudou = difere(atuais, gravados)
              pendenteDeSalvar.current = mudou
            }}
            onSubmit={handleSubmit}
          />
        </div>
      </section>

      <DiagnosticoParecer
        atendimento={atendimento}
        onAtualizar={onAtualizar}
        onSituacaoChange={setSituacaoNaTela}
        pendencia={pendencia}
        mostrarErro={faltaDiagnostico}
        tituloRef={tituloDiagnostico}
      />

      <div className={styles.rodape}>
        <div className='d-flex flex-column gap-1 fs-7 me-auto' aria-live='polite'>
          {!pendencia ? (
            <span className='d-flex align-items-center gap-2 text-gray-700'>
              <KTSVG path={IconPaths.CircleCheck} className='svg-icon-4 svg-icon-success' />
              Diagnóstico registrado
            </span>
          ) : (
            <span className='text-gray-800'>
              Falta para finalizar:{' '}
              <button
                type='button'
                className='btn btn-link p-0 fs-7 fw-semibold'
                onClick={irParaDiagnostico}
              >
                Diagnóstico
              </button>
            </span>
          )}
          {aviso && <span className='text-gray-800'>{aviso}</span>}
        </div>
        <Button
          type={ButtonType.Submit}
          form={FORM_ID}
          className='btn-primary'
          icon={IconPaths.Check}
          disabled={enviando}
        >
          {enviando ? <LoadingIndicator /> : 'Finalizar e assinar'}
        </Button>
      </div>
    </div>
  )
}
